import { useEffect, useRef, useState } from "preact/hooks";

import { ReminderToggleRow } from "../components/ReminderToggleRow";
import { SignOutButton } from "../components/SignOutButton";
import { Snackbar } from "../components/Snackbar";
import { useFocusHeadingOnMount } from "../hooks/useFocusHeadingOnMount";
import { useIsDesktop } from "../hooks/useIsDesktop";
import type { UseReminderPrefsResult } from "../hooks/useReminderPrefs";
import { SIGN_IN_SETTINGS_HEADING } from "../lib/authCopy";
import { SignInSettingsView } from "./SignInSettingsView";

export interface SettingsViewProps {
  reminderPrefs: UseReminderPrefsResult;
  /** Sprint-016 F3 (MORE-01, ux-audit-whole-app-2026-07-11.md — S3) —
   * `app.tsx` passes `() => navigate("/more")`, the SAME wiring every
   * other More-landing-row screen already receives
   * (`AnimalsScreen`/`ProjectsScreen`/`CalendarScreen`/`ReportsScreen`/
   * `ContactsScreen`). Below `1024px` this now MUST render a real
   * "‹ Back to More" affordance (this screen is reachable from a real
   * phone tap since this sprint — see `MoreView.tsx`) — every sibling
   * screen already has one; this was the one gap. */
  onBack: () => void;
  /** UX review 2026-10 (fix/ux-signin) — `useMe().logout`, threaded from
   * `app.tsx`; renders the confirm-first "Sign out" action at the bottom of
   * Your sign-in (every viewport). Optional only so isolated renders
   * needn't pass it; `app.tsx` always does. */
  onSignOut?: () => void | Promise<void>;
}

/**
 * Sprint-008 A2 (design-desktop-phase-a.md §1.2 row 9, §1.6) — "Your
 * sign-in" promoted from a `MoreView` subview (phone, unmodified — see
 * that file's own `subView === "sign-in"` branch) to its own routed,
 * top-level page (`/settings`, `lib/routes.ts`) so the desktop sidebar
 * can link straight to it. Label governed by R-A0-1 (UX validation) —
 * "Your sign-in," never "Settings" — `SIGN_IN_SETTINGS_HEADING` verbatim,
 * same string `MoreView`'s own subview heading already uses.
 *
 * Renders the exact same `SignInSettingsView` content `MoreView`'s
 * subview already shows, unchanged — no second version of that screen.
 *
 * `ReminderToggleRow` placement follows **m-A0-4** (UX non-blocking,
 * explicitly in this task's scope): on phone the toggle lives on the
 * More landing screen, a sibling of the sign-in subview, never nested in
 * it (`MoreView.tsx`). This page preserves that same conceptual
 * separation — the toggle renders in its own `.dashboard-card`-style
 * block with its own heading, below the sign-in content, never inside
 * the password/passkey block.
 *
 * Reachable on any viewport (not desktop-gated) — this used to be a
 * purely additive URL (no EXISTING phone tap routed here; `MoreView`'s
 * own "Your sign-in" row used local `subView` state). Sprint-016 F3
 * (MORE-01) repoints that row's `onClick` at this exact route (same
 * `navigate()` shape every sibling More-landing row already uses), so
 * this is now the ONE real phone entry point too — this screen picked up
 * the same phone-only "‹ Back to More" affordance every OTHER
 * More-landing screen already had (`AnimalsScreen.tsx`'s own docstring),
 * closing the one gap that promotion would otherwise have left.
 */
export function SettingsView({ reminderPrefs, onBack, onSignOut }: SettingsViewProps) {
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  useFocusHeadingOnMount(headingRef);
  const isDesktop = useIsDesktop();
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);

  // Same 5s auto-dismiss convention as every other local snackbar in this
  // app (design-spec-today-card.md §3.1).
  useEffect(() => {
    if (!snackbarMessage) return;
    const timer = setTimeout(() => setSnackbarMessage(null), 5000);
    return () => clearTimeout(timer);
  }, [snackbarMessage]);

  return (
    <section class="view" aria-label={SIGN_IN_SETTINGS_HEADING}>
      {isDesktop ? (
        <h1 ref={headingRef} tabIndex={-1} class="view-heading">
          {SIGN_IN_SETTINGS_HEADING}
        </h1>
      ) : (
        <div class="more-subheader">
          <button type="button" class="icon-btn" aria-label="Back to More" onClick={onBack}>
            ‹
          </button>
          <h1 ref={headingRef} tabIndex={-1} class="view-heading">
            {SIGN_IN_SETTINGS_HEADING}
          </h1>
        </div>
      )}

      <SignInSettingsView onNotify={setSnackbarMessage} />

      <div class="dashboard-card" style={{ marginTop: "24px" }}>
        <h2 class="dashboard-card-head">Daily reminders</h2>
        <ReminderToggleRow reminderPrefs={reminderPrefs} />
      </div>

      {onSignOut ? (
        <div class="sign-out-section">
          <SignOutButton onSignOut={onSignOut} />
        </div>
      ) : null}

      <Snackbar
        state={snackbarMessage ? { message: snackbarMessage } : null}
        onDismiss={() => setSnackbarMessage(null)}
      />
    </section>
  );
}
